// =============================================================================
//  Copyright : 2022 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Olli Powroznik
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : presentation support package
// =============================================================================

// ---Third-party imports
import React, {useEffect, useState} from 'react'
import clsx from 'clsx'

// ---Framework imports
import {size} from 'ldash'
import {Sel} from 'uix/registries'
import log from 'uix/logging'
import {useSelector, useRef} from 'uix/hooks'
import S from './audioticker.scss'

export const AudioWaves = ({active, hidden = true, onClick, muted, className}) => {
  return (
    <div
      onClick={onClick}
      className={clsx(S.loader, active && S.play, hidden && S.hidden, className)}
    >
      <span className={clsx(muted && S.muted)}></span>
      <svg
        className={S.wave}
        data-name="Layer 1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 50 38.05"
      >
        <title>Audio Wave</title>
        <path
          className={S.l1}
          data-name="Line 1"
          d="M0.91,15L0.78,15A1,1,0,0,0,0,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H0.91Z"
        />
        <path
          className={S.l2}
          data-name="Line 2"
          d="M6.91,9L6.78,9A1,1,0,0,0,6,10V28a1,1,0,1,0,2,0s0,0,0,0V10A1,1,0,0,0,7,9H6.91Z"
        />
        <path
          className={S.l3}
          data-name="Line 3"
          d="M12.91,0L12.78,0A1,1,0,0,0,12,1V37a1,1,0,1,0,2,0s0,0,0,0V1a1,1,0,0,0-1-1H12.91Z"
        />
        <path
          className={S.l4}
          data-name="Line 4"
          d="M18.91,10l-0.12,0A1,1,0,0,0,18,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H18.91Z"
        />
        <path
          className={S.l5}
          data-name="Line 5"
          d="M24.91,15l-0.12,0A1,1,0,0,0,24,16v6a1,1,0,0,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H24.91Z"
        />
        <path
          className={S.l6}
          data-name="Line 6"
          d="M30.91,10l-0.12,0A1,1,0,0,0,30,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H30.91Z"
        />
        <path
          className={S.l7}
          data-name="Line 7"
          d="M36.91,0L36.78,0A1,1,0,0,0,36,1V37a1,1,0,1,0,2,0s0,0,0,0V1a1,1,0,0,0-1-1H36.91Z"
        />
        <path
          className={S.l8}
          data-name="Line 8"
          d="M42.91,9L42.78,9A1,1,0,0,0,42,10V28a1,1,0,1,0,2,0s0,0,0,0V10a1,1,0,0,0-1-1H42.91Z"
        />
        <path
          className={S.l9}
          data-name="Line 9"
          d="M48.91,15l-0.12,0A1,1,0,0,0,48,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H48.91Z"
        />
      </svg>
    </div>
  )
}

export const AudioTicker = ({audio, audioDelay, started, playerState, setPlayerState}) => {
  const audioRef = useRef()
  // const audioCtx = new AudioContext()
  let apiurl = useSelector(Sel.apiurl)

  let {muted, playing, ended} = playerState

  useEffect(() => {
    let ref = audioRef.current
    // if (!audioCtx.state == 'suspended') {
    if (started && !ended) {
      setTimeout(() => {
        ref.play()
      }, audioDelay * 1000)
    } else {
      ref.load()
    }
    // }
  }, [started, audioRef.current, playing])

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = muted
    }
  }, [muted])

  let endHandler = () => {
    setPlayerState({ended: true})
  }

  let muteHandle = () => {
    setPlayerState({muted: !muted})
  }
  // do not access really short audio urls
  let isValidSource = size(audio) > 3
  return (
    <>
      <AudioWaves onClick={muteHandle} active={playing & !ended} muted={muted} />
      <audio preload="none" onEnded={endHandler} ref={audioRef}>
        {isValidSource && <source src={apiurl.href(audio)} />}
      </audio>
    </>
  )
}
